import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { GetAllExpeditionResponseDtoExpedition } from "@/types/api/api-types";
import { Button } from "@/components/ui/button";
import TrashOutlineIcon from "@/assets/icons/trash-outline.svg?react";
import EditOutlineIcon from "@/assets/icons/edit-outline.svg?react";
import { ActionData } from "@/types/tableAction";
import { CountryBadge } from "../shared/badge/country.badge";
import { formatDate } from "date-fns";
import { fr } from "date-fns/locale";
import { EllipsisVertical } from "lucide-react";
import { PendingShipmentsText } from "./pendingShipmentsText";

const columnHelper = createColumnHelper<GetAllExpeditionResponseDtoExpedition>();

type Props = {
  setActionData: (actionData: ActionData) => void;
  navigate: (path: string) => void;
};

export const pendingExpeditionsTableColumnsMobile = ({
  setActionData,
}: Props): ColumnDef<GetAllExpeditionResponseDtoExpedition>[] => {
  return [
    columnHelper.display({
      id: "identifier",
      header: () => (
        <PendingShipmentsText render={(t) => t("pendingShipmentsColumns.expedition")} />
      ),
      cell: (info) => {
        const client =
          info.row.original.deliveryInfo.company ||
          (info.row.original.deliveryInfo.firstName && info.row.original.deliveryInfo.lastName
            ? info.row.original.deliveryInfo.firstName +
              " " +
              info.row.original.deliveryInfo.lastName
            : "-");

        return (
          <button
            onClick={() =>
              setActionData({
                expedition: info.row.original,
                action: "MOBILE",
              })
            }
            className="flex w-full flex-row items-center justify-between"
          >
            <div className="flex flex-col gap-1">
              <div className="flex flex-row items-center gap-2">
                <p className="text-sm font-medium text-gray-900">{client}</p>
                <p className="text-xs font-normal text-gray-600">
                  {formatDate(info.row.original.createdAt, "dd MMM yyyy", {
                    locale: fr,
                  })}
                </p>
              </div>
              <div className="flex flex-row items-center gap-2">
                <p className="text-sm text-gray-700">
                  {info.row.original.quotation
                    ? "C-" + info.row.original.quotation?.identifier
                    : "D-" + info.row.original.customQuotation?.identifier}
                </p>
                <CountryBadge
                  country={info.row.original.deliveryInfo?.country || "-"}
                  className=""
                />
              </div>
              <p className="max-w-[200px] overflow-hidden text-start text-sm font-medium text-ellipsis text-gray-600">
                {info.row.original.createdBy
                  ? info.row.original.createdBy.firstName +
                    " " +
                    info.row.original.createdBy.lastName
                  : "-"}
              </p>
            </div>
            <EllipsisVertical className="h-5 w-5 text-gray-600" />
          </button>
        );
      },
    }),
  ];
};

export const pendingExpeditionsTableColumns = ({
  setActionData,
  navigate,
}: Props): ColumnDef<GetAllExpeditionResponseDtoExpedition>[] => {
  return [
    columnHelper.display({
      id: "identifier",
      header: () => (
        <PendingShipmentsText render={(t) => t("pendingShipmentsColumns.identifier")} />
      ),
      size: "auto" as unknown as number,
      cell: (info) => (
        <div className="max-w-[300px] truncate text-base">
          {info.row.original.quotation
            ? "C-" + info.row.original.quotation?.identifier
            : "D-" + info.row.original.customQuotation?.identifier}
        </div>
      ),
    }),
    columnHelper.display({
      id: "client",
      header: () => <PendingShipmentsText render={(t) => t("pendingShipmentsColumns.client")} />,
      cell: (info) => {
        const client =
          info.row.original.deliveryInfo.company ||
          (info.row.original.deliveryInfo.firstName && info.row.original.deliveryInfo.lastName
            ? info.row.original.deliveryInfo.firstName +
              " " +
              info.row.original.deliveryInfo.lastName
            : "-");
        return <div className="w-full max-w-[300px] truncate text-base font-bold">{client}</div>;
      },
    }),
    columnHelper.display({
      id: "country",
      header: () => <PendingShipmentsText render={(t) => t("pendingShipmentsColumns.country")} />,
      size: "auto" as unknown as number,
      cell: (info) => <CountryBadge country={info.row.original.deliveryInfo?.country || "-"} />,
    }),
    {
      id: "createdAt",
      accessorKey: "createdAt",
      header: () => <PendingShipmentsText render={(t) => t("pendingShipmentsColumns.createdAt")} />,
      sortDescFirst: true,
      size: "auto" as unknown as number,
      cell: (info) => (
        <div className="flex w-fit flex-col items-center gap-0">
          <p className="text-base text-gray-900">
            {new Date(info.row.original.createdAt).toLocaleDateString("fr-FR")}
          </p>
          <p className="text-sm text-gray-600">
            <PendingShipmentsText
              render={(t) =>
                t("pendingShipmentsColumns.createdAtTime", {
                  time: new Date(info.row.original.createdAt).toLocaleTimeString("fr-FR", {
                    hour: "2-digit",
                    minute: "2-digit",
                  }),
                })
              }
            />
          </p>
        </div>
      ),
    },
    columnHelper.display({
      id: "createdBy",
      header: () => <PendingShipmentsText render={(t) => t("pendingShipmentsColumns.createdBy")} />,
      size: "auto" as unknown as number,
      cell: (info) => (
        <div className="max-w-[300px] truncate text-base">
          {info.row.original.createdBy
            ? info.row.original.createdBy.firstName + " " + info.row.original.createdBy.lastName
            : "-"}
        </div>
      ),
    }),
    columnHelper.display({
      id: "actionOne",
      header: "",
      size: "auto" as unknown as number,
      cell: (info) => (
        <div className="flex flex-row items-center justify-center gap-3">
          <TrashOutlineIcon
            className="cursor-pointer text-slate-600 hover:text-slate-800"
            onClick={() => {
              setActionData({
                expedition: info.row.original,
                action: "DELETE",
              });
            }}
          />
          <EditOutlineIcon
            className="cursor-pointer text-slate-600 hover:text-slate-800"
            onClick={() => {
              if (info.row.original.quotation) {
                navigate(`/cotations/${info.row.original.id}`);
              } else {
                navigate(`/custom-cotations/${info.row.original.id}`);
              }
            }}
          />
        </div>
      ),
    }),
    columnHelper.display({
      id: "actions",
      header: "",
      size: "auto" as unknown as number,
      cell: (info) => {
        if (info.row.original.customQuotation)
          return (
            <Button
              variant="ghost"
              size="icon"
              disabled={true}
              className="w-fit px-2 text-base text-blue-700 hover:bg-transparent hover:text-blue-800"
            >
              <PendingShipmentsText render={(t) => t("pendingShipmentsColumns.quoteInProgress")} />
            </Button>
          );
        return (
          <Button
            variant="ghost"
            size="icon"
            className="w-fit px-2 text-base text-blue-700 hover:bg-transparent hover:text-blue-800"
            onClick={() => {
              setActionData({
                expedition: info.row.original,
                action: "FINALIZE",
              });
            }}
          >
            <PendingShipmentsText render={(t) => t("pendingShipmentsColumns.finalize")} />
          </Button>
        );
      },
    }),
  ];
};
